//
// LazyLoadManager — paginates a file list into ITEMS_PER_PAGE batches and renders
// them on demand via window.fileListManager. Coordinates:
//   - source resolution + optional shuffle (file-source-resolver)
//   - DOM indicators (indicators)
//   - loader status (loader-status)
//   - debug helpers (debug-helpers)
//
// Public contract (consumed across the app, do not break):
//   - export class LazyLoadManager
//   - export const lazyLoadManager     (singleton)
//   - window.lazyLoadManager           (same singleton)
//   - window.debugLazyLoad / forceLoadMore
//   - instance properties read by callers: allFiles, displayedFiles, currentOffset,
//     hasMore, isLoading, loadingIndicator, endIndicator, observer, container

import { ITEMS_PER_PAGE, LOAD_THRESHOLD, SEARCH_LIMIT } from './file-list.lazy-load/constants.js';
import { resolveFileList } from './file-list.lazy-load/file-source-resolver.js';
import {
    createIndicators,
    showLoadingIndicator,
    hideLoadingIndicator,
    setLoadingIndicatorIdle,
    showEndIndicator,
    hideEndIndicator
} from './file-list.lazy-load/indicators.js';
import { markFilesFilteredReady } from './file-list.lazy-load/loader-status.js';
import { registerDebugHelpers } from './file-list.lazy-load/debug-helpers.js';

function getDebugMode() {
    return (new URLSearchParams(window.location.search)).get('debug') || '0';
}

export class LazyLoadManager {
    constructor(containerSelector = '#file-list-container') {
        this.container = null;
        this.containerSelector = containerSelector;
        this.allFiles = [];
        this.displayedFiles = [];
        this.currentOffset = 0;
        this.isLoading = false;
        this.hasMore = true;
        this.observer = null;
        this.loadingIndicator = null;
        this.endIndicator = null;
        this.isReset = false;

        this.debugMode = new URLSearchParams(window.location.search).get('debug') === '1';
    }

    init() {
        this.container = document.querySelector(this.containerSelector);
        if (!this.container) {
            console.error('[LazyLoadManager] ❌ Container not found:', this.containerSelector);
            return this;
        }

        const indicators = createIndicators(this.container);
        if (indicators) {
            this.loadingIndicator = indicators.loadingIndicator;
            this.endIndicator = indicators.endIndicator;
        }

        this.setupIntersectionObserver();
        return this;
    }

    setupIntersectionObserver() {
        const options = {
            root: null,
            rootMargin: `${LOAD_THRESHOLD}px`,
            threshold: 0.1
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.isLoading && this.hasMore) {
                    this.loadMore();
                }
            });
        }, options);

        if (this.loadingIndicator) {
            this.observer.observe(this.loadingIndicator);
        } else {
            console.error('[LazyLoadManager] ❌ No loading indicator to observe!');
        }
    }

    // Replace the working file set with `files`, reset pagination, render first batch.
    setFiles(files) {
        const debugMode = getDebugMode();

        if (debugMode === '1') {
            console.log('[LazyLoadManager] setFiles called:',
                Array.isArray(files) ? files.length : 0, 'files',
                { isLoading: this.isLoading, currentOffset: this.currentOffset });
        }

        // Cancel previous in-flight load by dropping the loading flag.
        if (this.isLoading) {
            if (debugMode === '1') {
                console.log('[LazyLoadManager] Cancelling previous load, resetting state');
            }
            this.isLoading = false;
        }

        const limitedFiles = resolveFileList(files, debugMode);

        this.allFiles = limitedFiles;
        this.displayedFiles = [];
        this.currentOffset = 0;
        // hasMore=true if SEARCH_LIMIT was hit (more results exist beyond cap) OR there's anything to render.
        this.hasMore = this.allFiles.length === SEARCH_LIMIT || this.allFiles.length > 0;
        this.isLoading = false;
        this.isReset = true; // Mark that files were reset — first render must NOT append.

        if (debugMode === '1') {
            console.log('[LazyLoadManager] Files state reset:', {
                allFiles: this.allFiles.length,
                displayedFiles: this.displayedFiles.length,
                currentOffset: this.currentOffset,
                hasMore: this.hasMore,
                isLoading: this.isLoading,
                isReset: this.isReset
            });
        }

        // Disconnect observer to avoid races with rapid filter changes.
        if (this.observer) {
            this.observer.disconnect();
        }

        hideEndIndicator(this.endIndicator);

        if (!this.hasMore) {
            this._renderEmpty(debugMode);
            return this;
        }

        if (debugMode === '1') {
            console.log('[LazyLoadManager] Calling loadMore(true) with',
                this.allFiles.length, 'files, hasMore:', this.hasMore, 'isLoading:', this.isLoading);
        }
        this.loadMore(true);

        return this;
    }

    // Render an empty result and notify the loader.
    _renderEmpty(debugMode) {
        try {
            if (window.fileListManager && typeof window.fileListManager.renderFiles === 'function') {
                window.fileListManager.renderFiles([], false).then(() => {
                    markFilesFilteredReady(
                        'loader_stage_files_filtered_empty',
                        'Files filtered (empty result)',
                        debugMode
                    );
                }).catch(_ => {});
            }
            if (window.fileListManager && typeof window.fileListManager.updateFiles === 'function') {
                window.fileListManager.updateFiles([]);
            }
            if (window.fileListManager && typeof window.fileListManager.updateResultsCount === 'function') {
                window.fileListManager.updateResultsCount(0);
            }
        } catch (_) { /* noop */ }
        hideLoadingIndicator(this.loadingIndicator);
        showEndIndicator(this.endIndicator, this.displayedFiles.length, this.allFiles.length);
        this.isLoading = false;
    }

    // Load next batch. Also resumes a paused upstream search if needed.
    loadMore(isFirstLoad = false) {
        const debugMode = getDebugMode();

        if (this.isLoading || !this.hasMore) {
            if (debugMode === '1') {
                console.log('[LazyLoadManager] loadMore skipped:',
                    { isLoading: this.isLoading, hasMore: this.hasMore, isFirstLoad });
            }
            return;
        }

        if (debugMode === '1') {
            console.log('[LazyLoadManager] loadMore executing:', {
                isFirstLoad,
                allFiles: this.allFiles.length,
                displayedFiles: this.displayedFiles.length,
                currentOffset: this.currentOffset
            });
        }

        // Resume paused upstream search — gives us more allFiles to slice from.
        if (window.searchStateManager && window.searchStateManager._paused === true &&
            window.filterManager && typeof window.filterManager.continueSearch === 'function') {
            if (this.debugMode) {
                console.log('[SEARCH] Resuming paused search from LazyLoad');
            }
            window.filterManager.continueSearch();
            if (this.currentOffset < this.allFiles.length) {
                this.hasMore = true;
            } else {
                this.hasMore = false;
                this.isLoading = false;
                return;
            }
        }

        this.isLoading = true;

        if (!isFirstLoad) {
            showLoadingIndicator(this.loadingIndicator);
        }

        // First load fires immediately; subsequent loads have a tiny debounce.
        const loadDelay = isFirstLoad ? 0 : 50;

        setTimeout(() => {
            const nextBatch = this.allFiles.slice(
                this.currentOffset,
                this.currentOffset + ITEMS_PER_PAGE
            );

            if (nextBatch.length === 0) {
                // Upstream search may still be feeding — wait for continuation rather than ending.
                if (window.searchStateManager && window.searchStateManager._paused === true) {
                    this.hasMore = true;
                    this.isLoading = false;
                    return;
                }
                this.hasMore = false;
                hideLoadingIndicator(this.loadingIndicator);
                showEndIndicator(this.endIndicator, this.displayedFiles.length, this.allFiles.length);
                this.isLoading = false;
                return;
            }

            this.displayedFiles.push(...nextBatch);
            this.currentOffset += nextBatch.length;

            // hasMore: more in current array OR upstream cap means more behind it.
            this.hasMore = this.currentOffset < this.allFiles.length || this.allFiles.length === SEARCH_LIMIT;

            this._renderBatch(nextBatch, isFirstLoad, debugMode);

            // Reset flag after first render (used by _renderBatch above too).
            this.isReset = false;

            if (this.hasMore) {
                if (isFirstLoad) {
                    // Delay so filters have a chance to apply before re-arming the observer.
                    setTimeout(() => {
                        if (this.displayedFiles.length === ITEMS_PER_PAGE && this.allFiles.length >= ITEMS_PER_PAGE) {
                            setLoadingIndicatorIdle(this.loadingIndicator);
                            if (this.observer && this.loadingIndicator) {
                                this.observer.observe(this.loadingIndicator);
                            }
                        }
                    }, 500);
                } else {
                    setLoadingIndicatorIdle(this.loadingIndicator);
                    if (this.observer && this.loadingIndicator) {
                        this.observer.observe(this.loadingIndicator);
                    }
                }
            } else {
                hideLoadingIndicator(this.loadingIndicator);
                showEndIndicator(this.endIndicator, this.displayedFiles.length, this.allFiles.length);
            }

            this.isLoading = false;
        }, loadDelay);
    }

    // Push a batch through fileListManager.renderFiles, then update count, observer, and loader status.
    _renderBatch(nextBatch, isFirstLoad, debugMode) {
        const shouldAppend = !this.isReset;

        if (debugMode === '1') {
            console.log('[LazyLoadManager] About to render files:', {
                nextBatchLength: nextBatch.length,
                shouldAppend,
                isReset: this.isReset,
                hasFileListManager: !!window.fileListManager,
                hasRenderFiles: !!(window.fileListManager && typeof window.fileListManager.renderFiles === 'function')
            });
        }

        if (!(window.fileListManager && typeof window.fileListManager.renderFiles === 'function')) {
            console.error('[LazyLoadManager] ❌ fileListManager.renderFiles not available!', {
                hasFileListManager: !!window.fileListManager,
                hasRenderFiles: !!(window.fileListManager && typeof window.fileListManager.renderFiles === 'function'),
                windowKeys: Object.keys(window).filter(k => k.toLowerCase().includes('file'))
            });
            return;
        }

        window.fileListManager.renderFiles(nextBatch, shouldAppend).then(() => {
            // Allow append-mode for subsequent batches once first reset render is done.
            if (this.isReset && !shouldAppend) {
                this.isReset = false;
                if (debugMode === '1') {
                    console.log('[LazyLoadManager] isReset flag cleared after first render');
                }
            }

            if (debugMode === '1') {
                const container = window.jQuery ? window.jQuery('#file-list-container') : null;
                const actualElements = container ? container.find('.file-item').length : 0;
                console.log('[LazyLoadManager] renderFiles completed:', {
                    nextBatchLength: nextBatch.length,
                    shouldAppend,
                    isReset: this.isReset,
                    actualElementsInDOM: actualElements,
                    expectedElements: nextBatch.length
                });
            }

            // Update results count INSIDE render promise so it fires after render.js finishes.
            const hasMoreFiles = this.hasMore === true ||
                this.allFiles.length > this.displayedFiles.length ||
                this.allFiles.length === SEARCH_LIMIT;

            console.log('[LazyLoadManager] ✅ updateResultsCount INSIDE renderFiles.then():', {
                displayedFilesLength: this.displayedFiles.length,
                allFilesLength: this.allFiles.length,
                currentOffset: this.currentOffset,
                hasMoreFlag: this.hasMore,
                hasMoreFiles,
                condition1: this.hasMore === true,
                condition2: this.allFiles.length > this.displayedFiles.length,
                condition3: this.allFiles.length === SEARCH_LIMIT,
                willShowPlus: hasMoreFiles
            });

            if (window.fileListManager && typeof window.fileListManager.updateResultsCount === 'function') {
                window.fileListManager.updateResultsCount(this.displayedFiles.length, hasMoreFiles);
                console.log('[LazyLoadManager] ✅✅✅ updateResultsCount EXECUTED:', {
                    count: this.displayedFiles.length,
                    hasMore: hasMoreFiles,
                    display: `${this.displayedFiles.length}${hasMoreFiles ? '+' : ''}`,
                    allFiles: this.allFiles.length,
                    displayedFiles: this.displayedFiles.length,
                    hasMoreFlag: this.hasMore
                });
            } else {
                console.warn('[LazyLoadManager] fileListManager.updateResultsCount not available');
            }

            // Re-arm observer once DOM is updated.
            if (this.observer && this.loadingIndicator) {
                try {
                    this.observer.observe(this.loadingIndicator);
                    if (debugMode === '1') {
                        console.log('[LazyLoadManager] Observer reconnected after render');
                    }
                } catch (e) {
                    if (debugMode === '1') {
                        console.warn('[LazyLoadManager] Error reconnecting observer:', e);
                    }
                }
            }

            // Mark loader stage ready after first render so the global loader can finish.
            if (isFirstLoad) {
                markFilesFilteredReady(
                    'loader_stage_files_filtered_displayed',
                    'Files filtered and displayed',
                    debugMode
                );
            }
        }).catch(err => {
            console.error('[LazyLoadManager] ❌ Error in renderFiles:', err);
        });
    }

    reset() {
        this.allFiles = [];
        this.displayedFiles = [];
        this.currentOffset = 0;
        this.hasMore = true;
        this.isLoading = false;
        hideLoadingIndicator(this.loadingIndicator);
        hideEndIndicator(this.endIndicator);

        if (this.debugMode) {
            console.log('[LazyLoadManager] Reset');
        }
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        if (this.loadingIndicator && this.loadingIndicator.parentElement) {
            this.loadingIndicator.parentElement.removeChild(this.loadingIndicator);
        }
        if (this.endIndicator && this.endIndicator.parentElement) {
            this.endIndicator.parentElement.removeChild(this.endIndicator);
        }
        this.reset();
    }

    getState() {
        return {
            totalFiles: this.allFiles.length,
            displayedFiles: this.displayedFiles.length,
            currentOffset: this.currentOffset,
            hasMore: this.hasMore,
            isLoading: this.isLoading
        };
    }
}

// Singleton + global registration (must remain on this exact module path —
// imported from main-init/modules-initializer.js).
export const lazyLoadManager = new LazyLoadManager();
registerDebugHelpers(lazyLoadManager);
